// =============================================================================
// realtimeStatus — diagnostic callback for supabase.channel(...).subscribe()
//
// Without a callback, channel.subscribe() swallows CHANNEL_ERROR / TIMED_OUT and
// the only signal in DevTools is the browser's generic "WebSocket connection
// failed" line — which doesn't say whether the failure is server-side rejection
// (RLS, replication off, auth), a network timeout, or an explicit close.
//
// Pass `logChannelStatus(name)` as the callback to get a labelled console log
// per status transition + a Sentry breadcrumb. Persistent errors fire a
// captureMessage (deduped to once per 5 repeats) so they show up in Sentry
// but don't flood it.
//
// Usage:
//   .subscribe(logChannelStatus(`buyer-sub-orders-${user.id}`));
// =============================================================================
import * as Sentry from "@sentry/react";

export function logChannelStatus(channelName) {
  let errorRepeats = 0;
  return (status, err) => {
    const errMsg =
      err instanceof Error
        ? err.message
        : err
          ? String((err && err.message) || err)
          : undefined;
    const tail = errMsg ? ` — ${errMsg}` : "";
    // eslint-disable-next-line no-console
    console.log(`[Realtime/${channelName}] ${status}${tail}`);

    try {
      Sentry.addBreadcrumb({
        category: "realtime",
        level: status === "SUBSCRIBED" ? "info" : "warning",
        message: `${channelName} ${status}`,
        data: errMsg ? { error: errMsg } : undefined,
      });
    } catch { /* sentry is best-effort */ }

    if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
      errorRepeats += 1;
      if (errorRepeats === 1 || errorRepeats % 5 === 0) {
        try {
          Sentry.captureMessage(
            `Realtime ${status} on ${channelName}${errMsg ? `: ${errMsg}` : ""}`,
            "warning"
          );
        } catch { /* sentry is best-effort */ }
      }
    } else if (status === "SUBSCRIBED") {
      errorRepeats = 0;
    }
  };
}
